import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Drawer, Form, Input, Select, message, Space, Tooltip } from "antd";
import axios from "axios";
import {
  SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined
} from "@ant-design/icons";
import constants from "../../constant";

const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false); // Loading for table
  const [formLoading, setFormLoading] = useState(false); // Loading for form submit
  const [deleteLoadingId, setDeleteLoadingId] = useState(null); // Loading for specific delete button
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 100,
      position: ["topCenter"],
    },
    filters: {},
  });
  const searchInput = useRef(null);
  const [form] = Form.useForm();

  // Fetch users with optional filtering
  const fetchUsers = async () => {
    setLoading(true);
    try {
      let query = [];
      Object.keys(tableParams.filters).forEach((k) => {
        if (tableParams.filters[k]?.length > 0) {
          query.push(`${k}:sw=${tableParams.filters[k]}`);
        }
      });

      const res = await axios.get(`${constants.DOMAIN}/api/v1/systemuser/getUser?${query.join("&")}`);
      setUsers(res.data.data.users);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch users when filters change
  useEffect(() => {
    fetchUsers();
  }, [tableParams.filters]);

  // Update form fields when currentUser changes
  useEffect(() => {
    if (currentUser) {
      form.setFieldsValue({
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
      });
    } else {
      form.resetFields(); // Reset form when no current user
    }
  }, [currentUser, form]);

  // Open drawer for editing a user
  const handleEdit = (user) => {
    setCurrentUser(user);
    setIsDrawerOpen(true);
  };

  // Delete a user
  const handleDelete = async (id) => {
    setDeleteLoadingId(id);
    try {
      await axios.delete(`${constants.DOMAIN}/api/v1/systemuser/deleteUser/${id}`);
      message.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      message.error("Failed to delete user");
    } finally {
      setDeleteLoadingId(null);
    }
  };

  // Submit form (create or update user)
  const handleFormSubmit = async (values) => {
    setFormLoading(true);
    try {
      if (currentUser) {
        // Update existing user
        await axios.put(`${constants.DOMAIN}/api/v1/systemuser/updateUser/${currentUser._id}`, values);
        message.success("User updated successfully");
      } else {
        // Create new user
        await axios.post(`${constants.DOMAIN}/api/v1/systemuser/createUser`, values);
        message.success("User created successfully");
      }

      setIsDrawerOpen(false);
      fetchUsers();
      form.resetFields();
      setCurrentUser(null);
    } catch (error) {
      console.error("Error:", error);
      message.error(error.response?.data?.message || "Failed to save user");
    } finally {
      setFormLoading(false);
    }
  };

  // Search functionality for columns
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={close}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  // Handle search action
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setTableParams({
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
      filters: { [dataIndex]: selectedKeys },
    });
  };

  // Reset search filters
  const handleReset = (clearFilters) => {
    clearFilters();
    fetchUsers();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-4">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setCurrentUser(null);
            setIsDrawerOpen(true);
          }}
        >
          Add User
        </Button>
      </div>

      <Table
        dataSource={users}
        rowKey="_id"
        pagination={tableParams.pagination}
        loading={loading}
        columns={[
          {
            title: "Name",
            dataIndex: "name",
            ...getColumnSearchProps("name"),
          },
          {
            title: "Email",
            dataIndex: "email",
            ...getColumnSearchProps("email"),
          },
          {
            title: "Role",
            dataIndex: "role",
          },
          {
            title: "Actions",
            render: (text, record) => (
              <Space size="middle">
                <Tooltip title="Edit">
                  <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleteLoadingId === record._id}
                    onClick={() => handleDelete(record._id)}
                  />
                </Tooltip>
                <Tooltip title="Preview">
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => message.info(JSON.stringify(record, null, 2))}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={currentUser ? "Edit User" : "Add User"}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setCurrentUser(null);
          form.resetFields();
        }}
        width={400}
      >
        <Form
          form={form}
          initialValues={currentUser || { name: "", email: "", role: "" }}
          onFinish={handleFormSubmit}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please input the user name!" }]}
          >
            <Input placeholder="Enter user name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please input the user email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input placeholder="Enter user email" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please select a role!" }]}
          >
            <Select placeholder="Select user role">
              <Option value="ADMIN">ADMIN</Option>
              <Option value="SALES">SALES</Option>
              <Option value="DESIGN">DESIGN</Option>
              <Option value="PRODUCTION">PRODUCTION</Option>
              <Option value="DELIVERY">DELIVERY</Option>
              <Option value="ACCOUNTS">ACCOUNTS</Option>
            </Select>
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={formLoading}>
            Save
          </Button>
        </Form>
      </Drawer>
    </div>
  );
};

export default UserManagement;
